import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useHistory } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { stringify } from 'query-string';

import { useSidebarLayout } from 'providers/root/SidebarLayoutProvider';
import { useMenuScopeIdentifier } from 'hooks/menuScope';
import { getScopeBasedTranslationKeys } from 'utils/menuMaker/scopeTranslations';

import Box from '@mui/material/Box';

import HeaderForm from 'components/HeaderForm';
import DisplayMessage from 'components/DisplayMessageV2';
import PageError from 'components/PageError';
import MenuMakerTable from 'components/MenuMakerTable';
import HelperSkeleton from 'components/MenuMakerTable/HelperSkeleton';

import { drawerActions, drawerEntities } from 'utils/drawerMenuMaker';

import { useCommandBarMenus } from 'hooks/useCommandBarAttributes';
import TitleSection from 'components/TitleSection';

import { EmptyMenus } from 'containers/MenuMakerResume/EmptyMenus';

import useStyles from './styles';

function MenusContent({
  loading,
  menusError,
  menus,
  isSearchFilter,
  onReloadMenus,
  onPaginate,
  openDrawer,
  menusPagination,
  paginationState,
  onSearchMenusByText,
  onCopyMenu,
  onDeleteMenu,
  onDuplicate,
  deleteMenuState,
}) {
  const { modeDraft, isPublishMenuError } = useSidebarLayout();
  const classes = useStyles({ openDrawer, isModeDraft: modeDraft || isPublishMenuError });
  const { t } = useTranslation('menuMaker');
  const commandBarHook = useCommandBarMenus();
  const { isStoreMode, isOrganizationScope } = useMenuScopeIdentifier();

  const history = useHistory();

  const scrollPaginationRef = useRef(null);

  // Show "Add Menu" functionality only when NOT in restrictive store mode
  const shouldShowAddMenu = !isStoreMode;

  // Get scope-based translation keys
  const { titleKey, descriptionKey } = getScopeBasedTranslationKeys(isOrganizationScope, 'menus');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const { isIntersecting } = entries[0];
        if (isIntersecting && menusPagination?.next) {
          if (!paginationState.fetching) {
            onPaginate();
          }
        }
      },
      { threshold: 0 },
    );

    if (scrollPaginationRef.current) observer.observe(scrollPaginationRef.current);

    return () => {
      observer.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPaginationRef, menusPagination?.next]);

  const handleOpenNewMenu = () => {
    const query = { action: drawerActions.CREATE, entity: drawerEntities.MENU };

    history.replace({ search: stringify(query) });
  };

  function renderContent() {
    if (loading) {
      return <HelperSkeleton dragActive={!isSearchFilter} />;
    }

    if (menusError) {
      return (
        <Box className={classes.containerMessageError}>
          <PageError
            data-testid="menusLoadError"
            id="menusLoadError"
            labelAction={t('common:buttons.retry')}
            maxWidth={300}
            message={t('common:errors.loaded.errorConnection')}
            onAction={onReloadMenus}
            title={t('common:errors.sorry')}
          />
        </Box>
      );
    }

    if (!menus?.length && !loading && isSearchFilter) {
      return (
        <DisplayMessage
          data-testid="menusSearchNoData"
          id="menusSearchNoData"
          maxWidth={450}
          message={t('common:messages.searchMessageNoResults')}
          sizeIcon={120}
          title={t('common:messages.searchTitleNoResults')}
        />
      );
    }

    if (!menus?.length && !loading && !isSearchFilter) {
      return <EmptyMenus />;
    }

    return (
      <MenuMakerTable
        deleteMenuState={deleteMenuState}
        isSearchFilter={isSearchFilter}
        loadingPagination={paginationState.fetching}
        menus={menus}
        onCopyMenu={onCopyMenu}
        onDeleteMenu={onDeleteMenu}
        onDuplicate={onDuplicate}
        reloadMenu={onReloadMenus}
        scrollPaginationRef={scrollPaginationRef}
      />
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.contentMenus}>
        <TitleSection
          commandBarHook={commandBarHook}
          description={<Trans i18nKey={descriptionKey} />}
          title={t(titleKey)}
        />
        <HeaderForm
          addButtonLabel={shouldShowAddMenu ? t('menuMaker:buttons.menu') : undefined}
          isShowFilterBrands
          onAddButtonClick={shouldShowAddMenu ? handleOpenNewMenu : undefined}
          onSearchBoxChange={onSearchMenusByText}
          openDrawer={openDrawer}
          px={5}
          searchLabel={t('menuMaker:headerForm.searchMenu')}
        />
        {renderContent()}
      </Box>
    </Box>
  );
}

MenusContent.propTypes = {
  loading: PropTypes.bool,
  menusError: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool]),
  menus: PropTypes.array,
  isSearchFilter: PropTypes.bool,
  onReloadMenus: PropTypes.func,
  onPaginate: PropTypes.func,
  openDrawer: PropTypes.bool,
  menusPagination: PropTypes.object,
  paginationState: PropTypes.object,
  onSearchMenusByText: PropTypes.func,
  onCopyMenu: PropTypes.func,
  onDeleteMenu: PropTypes.func,
  onDuplicate: PropTypes.func,
  deleteMenuState: PropTypes.object,
};

export default MenusContent;
